import React, {
    useEffect,
    useMemo,
    useState
} from 'react';
import { Link } from 'react-router-dom';
import './faq.css';
import API_BASE_URL from '../../config/api';
import SupportMenu from './SupportMenu';

const CATEGORIES = [
    '전체',
    'LED 전광판',
    '빔프로젝터',
    '예약·결제',
    '설치·운영'
];

const ITEMS_PER_PAGE = 10;

const Faq = () => {
    const [faqs, setFaqs] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('전체');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [openFaqId, setOpenFaqId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchFaqs = async () => {
            try {
                setLoading(true);
                setError('');

                const response = await fetch(
                    `${API_BASE_URL}/api/faqs`
                );

                if (!response.ok) {
                    throw new Error(
                        'FAQ 조회에 실패했습니다.'
                    );
                }

                const data =
                    await response.json();

                setFaqs(data);
            } catch (error) {
                console.error(
                    'FAQ 조회 오류:',
                    error
                );

                setError(
                    '자주 묻는 질문을 불러오지 못했습니다.'
                );
            } finally {
                setLoading(false);
            }
        };

        fetchFaqs();
    }, []);

    const filteredFaqs = useMemo(() => {
        const keyword =
            searchKeyword
                .trim()
                .toLowerCase();

        return faqs.filter((faq) => {
            const matchesCategory =
                selectedCategory === '전체' ||
                faq.category ===
                    selectedCategory;

            if (!matchesCategory) {
                return false;
            }

            if (!keyword) {
                return true;
            }

            const searchableText = [
                faq.category,
                faq.question,
                faq.answer
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return searchableText.includes(
                keyword
            );
        });
    }, [
        faqs,
        selectedCategory,
        searchKeyword
    ]);

    const totalPages = Math.max(
        1,
        Math.ceil(
            filteredFaqs.length /
                ITEMS_PER_PAGE
        )
    );

    const paginatedFaqs = useMemo(() => {
        const startIndex =
            (currentPage - 1) *
            ITEMS_PER_PAGE;

        const endIndex =
            startIndex +
            ITEMS_PER_PAGE;

        return filteredFaqs.slice(
            startIndex,
            endIndex
        );
    }, [
        filteredFaqs,
        currentPage
    ]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(
                totalPages
            );
        }
    }, [
        currentPage,
        totalPages
    ]);

    const pageNumbers = useMemo(() => {
        if (totalPages <= 5) {
            return Array.from(
                {
                    length: totalPages
                },
                (_, index) =>
                    index + 1
            );
        }

        let startPage = Math.max(
            1,
            currentPage - 2
        );

        const endPage = Math.min(
            totalPages,
            startPage + 4
        );

        if (
            endPage - startPage <
            4
        ) {
            startPage = Math.max(
                1,
                endPage - 4
            );
        }

        return Array.from(
            {
                length:
                    endPage -
                    startPage +
                    1
            },
            (_, index) =>
                startPage + index
        );
    }, [
        currentPage,
        totalPages
    ]);

    const handleCategory = (
        category
    ) => {
        setSelectedCategory(
            category
        );
        setCurrentPage(1);
        setOpenFaqId(null);
    };

    const handleSearchChange = (
        event
    ) => {
        setSearchKeyword(
            event.target.value
        );
        setCurrentPage(1);
        setOpenFaqId(null);
    };

    const handleClearSearch = () => {
        setSearchKeyword('');
        setCurrentPage(1);
        setOpenFaqId(null);
    };

    const handlePageChange = (
        page
    ) => {
        setCurrentPage(page);
        setOpenFaqId(null);
    };

    const handleFaq = (
        faqId
    ) => {
        setOpenFaqId(
            (currentId) =>
                currentId === faqId
                    ? null
                    : faqId
        );
    };

    return (
        <div className="faq-page">
            <section className="faq-sub-visual">
                <div className="faq-sub-overlay"></div>

                <div className="faq-sub-inner">
                    <p>
                        CUSTOMER SUPPORT
                    </p>

                    <h1>
                        자주 묻는 질문
                    </h1>

                    <div className="faq-breadcrumb">
                        <Link to="/">
                            HOME
                        </Link>

                        <span>
                            ›
                        </span>

                        <strong>
                            자주 묻는 질문
                        </strong>
                    </div>
                </div>
            </section>

            <SupportMenu />

            <section className="faq-section">
                <div className="faq-container">
                    <div className="faq-heading">
                        <p>
                            FAQ
                        </p>

                        <h2>
                            자주 묻는 질문
                        </h2>

                        <span>
                            장비 렌탈과 이용에 대해 자주 문의하시는 내용을 안내드립니다.
                        </span>
                    </div>

                    <div className="faq-categories">
                        {CATEGORIES.map(
                            (category) => (
                                <button
                                    type="button"
                                    key={category}
                                    className={
                                        selectedCategory ===
                                        category
                                            ? 'active'
                                            : ''
                                    }
                                    onClick={() =>
                                        handleCategory(
                                            category
                                        )
                                    }
                                >
                                    {category}
                                </button>
                            )
                        )}
                    </div>

                    <div className="faq-search-wrap">
                        <div className="faq-search">
                            <i className="mdi mdi-magnify"></i>

                            <input
                                type="text"
                                value={searchKeyword}
                                onChange={
                                    handleSearchChange
                                }
                                placeholder="질문이나 답변을 검색해주세요."
                                aria-label="FAQ 검색"
                            />

                            {searchKeyword && (
                                <button
                                    type="button"
                                    className="faq-search-clear"
                                    onClick={
                                        handleClearSearch
                                    }
                                    aria-label="검색어 지우기"
                                >
                                    <i className="mdi mdi-close"></i>
                                </button>
                            )}
                        </div>

                        <span className="faq-result-count">
                            총{' '}
                            <strong>
                                {
                                    filteredFaqs.length
                                }
                            </strong>
                            개
                        </span>
                    </div>

                    <div className="faq-list">
                        {loading ? (
                            <div className="faq-state">
                                자주 묻는 질문을 불러오는 중입니다.
                            </div>
                        ) : error ? (
                            <div className="faq-state">
                                {error}
                            </div>
                        ) : filteredFaqs.length > 0 ? (
                            paginatedFaqs.map(
                                (faq) => {
                                    const isOpen =
                                        openFaqId ===
                                        faq.faq_id;

                                    return (
                                        <div
                                            className={
                                                isOpen
                                                    ? 'faq-item active'
                                                    : 'faq-item'
                                            }
                                            key={
                                                faq.faq_id
                                            }
                                        >
                                            <button
                                                type="button"
                                                className="faq-question"
                                                onClick={() =>
                                                    handleFaq(
                                                        faq.faq_id
                                                    )
                                                }
                                                aria-expanded={
                                                    isOpen
                                                }
                                            >
                                                <span className="faq-question-mark">
                                                    Q
                                                </span>

                                                <div className="faq-question-text">
                                                    <span>
                                                        {
                                                            faq.category
                                                        }
                                                    </span>

                                                    <strong>
                                                        {
                                                            faq.question
                                                        }
                                                    </strong>
                                                </div>

                                                <i
                                                    className={
                                                        isOpen
                                                            ? 'mdi mdi-minus'
                                                            : 'mdi mdi-plus'
                                                    }
                                                ></i>
                                            </button>

                                            {isOpen && (
                                                <div className="faq-answer">
                                                    <span className="faq-answer-mark">
                                                        A
                                                    </span>

                                                    <p>
                                                        {
                                                            faq.answer
                                                        }
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                            )
                        ) : (
                            <div className="faq-state">
                                {searchKeyword.trim()
                                    ? '검색 결과가 없습니다.'
                                    : '등록된 질문이 없습니다.'
                                }
                            </div>
                        )}
                    </div>

                    {!loading &&
                        !error &&
                        filteredFaqs.length > 0 &&
                        totalPages > 1 && (
                            <div className="faq-pagination">
                                <button
                                    type="button"
                                    className="faq-pagination-arrow"
                                    onClick={() =>
                                        handlePageChange(
                                            Math.max(
                                                currentPage -
                                                    1,
                                                1
                                            )
                                        )
                                    }
                                    disabled={
                                        currentPage ===
                                        1
                                    }
                                    aria-label="이전 페이지"
                                >
                                    <i className="mdi mdi-chevron-left"></i>
                                </button>

                                <div className="faq-pagination-pages">
                                    {pageNumbers.map(
                                        (page) => (
                                            <button
                                                type="button"
                                                key={page}
                                                className={
                                                    currentPage ===
                                                    page
                                                        ? 'active'
                                                        : ''
                                                }
                                                onClick={() =>
                                                    handlePageChange(
                                                        page
                                                    )
                                                }
                                            >
                                                {page}
                                            </button>
                                        )
                                    )}
                                </div>

                                <button
                                    type="button"
                                    className="faq-pagination-arrow"
                                    onClick={() =>
                                        handlePageChange(
                                            Math.min(
                                                currentPage +
                                                    1,
                                                totalPages
                                            )
                                        )
                                    }
                                    disabled={
                                        currentPage ===
                                        totalPages
                                    }
                                    aria-label="다음 페이지"
                                >
                                    <i className="mdi mdi-chevron-right"></i>
                                </button>
                            </div>
                        )}

                    <div className="faq-inquiry">
                        <div>
                            <p>
                                원하는 답변을 찾지 못하셨나요?
                            </p>

                            <h3>
                                행사에 필요한 장비를
                                직접 상담해보세요.
                            </h3>

                            <span>
                                행사 일정과 장소를 알려주시면
                                필요한 장비 구성을 안내해드립니다.
                            </span>
                        </div>

                        <Link to="/inquiry">
                            견적문의
                            <i className="mdi mdi-arrow-right"></i>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Faq;